import * as React from "react";
import {Col, ControlLabel, FormControl, FormGroup, OverlayTrigger, Tooltip} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {
  DatabaseType,
  DBConnectionConfig,
  EMPTY_ORACLE_DB_CONNECTION,
  EMPTY_POSTGIS_DB_CONNECTION,
  EMPTY_SQLSERVER_DB_CONNECTION
} from "../settings/model";

interface DatabaseConnectionChooserOwnProps {
  onChange: (event: { target: { name: "databaseConnection"; value: DBConnectionConfig; } }) => void;
}

type DatabaseConnectionChooserProps = DatabaseConnectionChooserOwnProps & WithApiProperties & InjectedIntlProps;

interface DatabaseConnectionChooserState {
  isLoading: boolean;
  allConnections: DBConnectionConfig[];
  selectedConnection: DBConnectionConfig;
}

const MESSAGES = defineMessages({
  createNewOracleConnection: {
    id: "studio.data.database-connections-chooser.create-new-oracle-connection",
    defaultMessage: "Create a new Oracle connection...",
  },
  createNewPostgisConnection: {
    id: "studio.data.database-connections-chooser.create-new-postgis-connection",
    defaultMessage: "Create a new PostGIS connection...",
  },
  createNewSqlServerConnection: {
    id: "studio.data.database-connections-chooser.create-new-sqlserver-connection",
    defaultMessage: "Create a new SQL Server connection...",
  },
  HELP: {
    id: "studio.UI.help-alt",
    defaultMessage: "❔",
  },

});

const LABEL_COL = 3;
const CONTROL_COL = 8;
const HELP_COL = 1;

class DatabaseConnectionChooserComponent extends React.Component<DatabaseConnectionChooserProps, DatabaseConnectionChooserState> {

  constructor(props: DatabaseConnectionChooserProps) {
    super(props);
    this.state = {
      isLoading: false,
      allConnections: [],
      selectedConnection: null,
    };
  }

  componentDidMount() {
    const allConnections = [
      EMPTY_ORACLE_DB_CONNECTION,
      EMPTY_POSTGIS_DB_CONNECTION,
      EMPTY_SQLSERVER_DB_CONNECTION,
    ];
    this.setState({
      isLoading: false,
      allConnections,
      selectedConnection: EMPTY_ORACLE_DB_CONNECTION,
    });
  }

  updateState = (updatedPart) => this.setState(Object.assign({}, this.state, updatedPart));

  render() {
    const {isLoading, allConnections, selectedConnection} = this.state;
    const {intl} = this.props;

    return <React.Fragment>
      <FormGroup>
        <Col componentClass={ControlLabel} sm={LABEL_COL}>
          <FormattedMessage
              id="studio.crawl-data-dialog.db-connection"
              defaultMessage="Connection"/>
        </Col>
        <Col sm={CONTROL_COL}>
          <FormControl name="selectedConnection" value={this.getConnectionOptionValue(selectedConnection)}
                       onChange={this.onDatabaseConnectionChange} componentClass="select">
            {isLoading &&
             <option><FormattedMessage id="studio.database-connection-chooser.loading" defaultMessage="Loading..."/>
             </option>}
            {allConnections.map((c) => {
              const value = this.getConnectionOptionValue(c);
              const text = this.getConnectionOptionText(c);

              return <option key={value} value={value}>{text}</option>
            })}
          </FormControl>
        </Col>
        <Col sm={HELP_COL} className={"databaseConnectionForm-Label"}>
          <OverlayTrigger placement="top" overlay={<Tooltip
              id="tooltip_help">
            <FormattedMessage id="studio.add-data.database-connection.type.help"
                              defaultMessage="Database type"/>
          </Tooltip>}>
            <img src="../theme/assets/images/help_16.png" id="database-type"
                 alt={intl.formatMessage(MESSAGES.HELP)}
                 onMouseOver={this.displayHelp}
                 onMouseLeave={this.displayHelp}/>
          </OverlayTrigger>
        </Col>
      </FormGroup>
    </React.Fragment>;
  }

  private displayHelp = (event: any) => {
    if (event.type === "mouseover") {
      this.updateState({displayHelp: event.target.id});
    } else {
      this.updateState({displayHelp: ""});
    }
  }

  private getConnectionOptionValue(connection: DBConnectionConfig) {
    if (!connection) {
      return "";
    }

    return `${connection.type}-${connection.connectionName}`;
  }

  private getConnectionOptionText(connection: DBConnectionConfig) {
    const {intl} = this.props;

    if (connection.id === null && connection.type === DatabaseType.ORACLE) {
      return intl.formatMessage(MESSAGES.createNewOracleConnection);
    }

    if (connection.id === null && connection.type === DatabaseType.POSTGIS) {
      return intl.formatMessage(MESSAGES.createNewPostgisConnection);
    }

    if (connection.id === null && connection.type === DatabaseType.SQLSERVER) {
      return intl.formatMessage(MESSAGES.createNewSqlServerConnection);
    }

    return connection.connectionName;
  }

  private onDatabaseConnectionChange = (event: any) => {
    const {onChange} = this.props;
    const {allConnections} = this.state;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    const selectedConnection = allConnections.find(c => this.getConnectionOptionValue(c) === value)

    this.updateState({
      selectedConnection,
    });
    onChange({target: {name: "databaseConnection", value: selectedConnection}});
  }
}

export const DatabaseConnectionChooser = WithApi(injectIntl(DatabaseConnectionChooserComponent));